import { Dispatch, FC, SetStateAction } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useThemeColors } from 'hooks/useThemeColors'

/* Utils */
import { AnalysisDataTable, IntegrationInstance } from 'models'

/* Components */
import { Drawer } from './settings.styles'
import RowDataItem from './RowDataItem'
import CloseChipIcon from '../../../../components/svg/CloseChipIcon'

interface Props {
  drawerOpen: boolean
  activeRowItem: AnalysisDataTable | null
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  firstInstance: IntegrationInstance | undefined
  secondInstance: IntegrationInstance | undefined
  isCompareToAll?: boolean
  instances?: IntegrationInstance[]
}

const RowDataDrawer: FC<Props> = ({
  drawerOpen,
  activeRowItem,
  setDrawerOpen,
  firstInstance,
  secondInstance,
  isCompareToAll,
  instances = []
}) => {
  const getColor = useThemeColors()
  if (!activeRowItem) return <></>

  /* A field row is hidden entirely when no source/record has a value for it. */
  const hasValue = (value: unknown): boolean => (Array.isArray(value) ? value.some(Boolean) : Boolean(value))
  const hasLastSeen = (value?: string | null): boolean => (value ? !value.includes('0001-01-01') : false)

  /* Compare-to-All: the row carries every contributing record (A = picked product, B = all others).
   * Render one entry per record for each field, resolving the instance from its slug. */
  if (isCompareToAll) {
    const records = [...(activeRowItem.matchingRecordsA || []), ...(activeRowItem.matchingRecordsB || [])]
    const instanceFor = (slug: string) => instances.find((n) => n.instance_name === slug)
    const EMPTY = '━━'

    const showHostName = records.some((r) => hasValue(r.hostname))
    const showSerialNumber = records.some((r) => hasValue(r.serial_number))
    const showMacAddress = records.some((r) => hasValue(r.mac_addresses))
    const showLastSeen = records.some((r) => hasLastSeen(r.last_seen))
    const showOperatingSystem = records.some((r) => hasValue(r.operating_system))
    const showAssetId = records.some((r) => hasValue(r.asset_id))

    return (
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box role="presentation">
          <Box className="wrapper-inner">
            <Box className="header">
              <Typography className="title">Asset Details</Typography>
              <IconButton className="close-icon" onClick={() => setDrawerOpen(false)}>
                <CloseChipIcon color={getColor('text-secondary')} />
              </IconButton>
            </Box>

            <Box className="content">
              <Box className="row-item">
                <Typography className="row-name">Coverage</Typography>
                <Box className="row-data">
                  {records.map((record, index) => {
                    const inst = instanceFor(record.instance)
                    return <RowDataItem key={index} instance={inst} text={inst?.name || record.instance} />
                  })}
                </Box>
              </Box>

              {showHostName && (
                <Box className="row-item">
                  <Typography className="row-name">Host name</Typography>
                  <Box className="row-data">
                    {records.map((record, index) => (
                      <RowDataItem
                        key={index}
                        instance={instanceFor(record.instance)}
                        text={record.hostname || EMPTY}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {showSerialNumber && (
                <Box className="row-item">
                  <Typography className="row-name">Serial number</Typography>
                  <Box className="row-data">
                    {records.map((record, index) => (
                      <RowDataItem
                        key={index}
                        instance={instanceFor(record.instance)}
                        text={record.serial_number || EMPTY}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {showMacAddress && (
                <Box className="row-item">
                  <Typography className="row-name">MAC address</Typography>
                  <Box className="row-data">
                    {records.map((record) => {
                      const inst = instanceFor(record.instance)
                      const macs = record.mac_addresses?.length ? record.mac_addresses : [EMPTY]
                      return macs.map((mac, macIndex) =>
                        macIndex === 0 ? (
                          <RowDataItem key={`${record.instance}-${macIndex}`} instance={inst} text={mac} />
                        ) : (
                          <Box key={`${record.instance}-${macIndex}`} sx={{ pl: '32px' }}>
                            <RowDataItem instance={inst} text={mac} hideImg />
                          </Box>
                        )
                      )
                    })}
                  </Box>
                </Box>
              )}

              {showLastSeen && (
                <Box className="row-item">
                  <Typography className="row-name">Last seen</Typography>
                  <Box className="row-data">
                    {records.map((record, index) => (
                      <RowDataItem
                        key={index}
                        instance={instanceFor(record.instance)}
                        text={record.last_seen ? dayjs(record.last_seen).format('YYYY-MM-DD HH:MM:ss') : EMPTY}
                        lastSeenMissing={!record.last_seen}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {showOperatingSystem && (
                <Box className="row-item">
                  <Typography className="row-name">Operating system</Typography>
                  <Box className="row-data">
                    {records.map((record, index) => (
                      <RowDataItem
                        key={index}
                        instance={instanceFor(record.instance)}
                        text={record.operating_system || EMPTY}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {showAssetId && (
                <Box className="row-item">
                  <Typography className="row-name">Asset ID</Typography>
                  <Box className="row-data">
                    {records.map((record, index) => (
                      <RowDataItem
                        key={index}
                        instance={instanceFor(record.instance)}
                        text={record.asset_id || EMPTY}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>
    )
  }
  const coverage = firstInstance?.is_v2
    ? activeRowItem.coverage
    : activeRowItem.coverage === 'both'
    ? 'both'
    : activeRowItem.coverage.slice(0, -9)
  let thisInstance =
    firstInstance && firstInstance.integration_name === coverage
      ? firstInstance
      : secondInstance && secondInstance.integration_name === coverage
      ? secondInstance
      : null
  if (firstInstance?.is_v2) {
    thisInstance =
      firstInstance && firstInstance.instance_name === coverage
        ? firstInstance
        : secondInstance && secondInstance.is_v2 && secondInstance.instance_name === coverage
        ? secondInstance
        : null
  }

  const showHostName = hasValue(activeRowItem.hostName) || hasValue(activeRowItem.secondHostName)
  const showSerialNumber = hasValue(activeRowItem.serialNumber) || hasValue(activeRowItem.secondSerialNumber)
  const showMacAddress = hasValue(activeRowItem.macAddress) || hasValue(activeRowItem.secondMacAddress)
  const showLastSeen = hasLastSeen(activeRowItem.lastSeen) || hasLastSeen(activeRowItem.secondLastSeen)
  const showOperatingSystem = hasValue(activeRowItem.operatingSystem) || hasValue(activeRowItem.secondOperatingSystem)
  const showAssetId = hasValue(activeRowItem.assetId) || hasValue(activeRowItem.secondAssetId)

  return (
    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      <Box role="presentation">
        <Box className="wrapper-inner">
          <Box className="header">
            <Typography className="title">Asset Details</Typography>
            <IconButton className="close-icon" onClick={() => setDrawerOpen(false)}>
              <CloseChipIcon color={getColor('text-secondary')} />
            </IconButton>
          </Box>

          <Box className="content">
            <Box className="row-item">
              <Typography className="row-name">Coverage</Typography>
              <Box className="row-data">
                {thisInstance && coverage !== 'both' ? (
                  <RowDataItem instance={thisInstance} text={thisInstance.name || ''} />
                ) : (
                  <>
                    <RowDataItem instance={firstInstance} text={firstInstance?.name || ''} />
                    <RowDataItem instance={secondInstance} text={secondInstance?.name || ''} />
                  </>
                )}
              </Box>
            </Box>

            {showHostName && (
              <Box className="row-item">
                <Typography className="row-name">Host name</Typography>
                <Box className="row-data">
                  {thisInstance && coverage !== 'both' ? (
                    <RowDataItem instance={thisInstance} text={activeRowItem?.hostName || ''} />
                  ) : (
                    <>
                      {activeRowItem?.hostName && (
                        <RowDataItem instance={firstInstance} text={activeRowItem.hostName} />
                      )}
                      {activeRowItem?.secondHostName && (
                        <RowDataItem instance={secondInstance} text={activeRowItem.secondHostName} />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {showSerialNumber && (
              <Box className="row-item">
                <Typography className="row-name">Serial number</Typography>
                <Box className="row-data">
                  {thisInstance && coverage !== 'both' ? (
                    <RowDataItem instance={thisInstance} text={activeRowItem?.serialNumber || ''} />
                  ) : (
                    <>
                      {activeRowItem?.serialNumber && (
                        <RowDataItem instance={firstInstance} text={activeRowItem.serialNumber} />
                      )}
                      {activeRowItem?.secondSerialNumber && (
                        <RowDataItem instance={secondInstance} text={activeRowItem.secondSerialNumber} />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {showMacAddress && (
              <Box className="row-item">
                <Typography className="row-name">MAC address</Typography>
                <Box className="row-data">
                  {thisInstance && coverage !== 'both' ? (
                    Array.isArray(activeRowItem?.macAddress) ? (
                      activeRowItem.macAddress.map((mac, index) =>
                        index === 0 ? (
                          <RowDataItem key={index} instance={thisInstance ?? undefined} text={mac || ''} />
                        ) : (
                          <Box key={index} sx={{ pl: '32px' }}>
                            <RowDataItem instance={thisInstance ?? undefined} text={mac || ''} hideImg />
                          </Box>
                        )
                      )
                    ) : (
                      <RowDataItem instance={thisInstance} text={activeRowItem?.macAddress || ''} />
                    )
                  ) : (
                    <>
                      {Array.isArray(activeRowItem?.macAddress) && activeRowItem.macAddress.length > 0 ? (
                        activeRowItem.macAddress.map((mac, index) =>
                          index === 0 ? (
                            <RowDataItem key={`first-${index}`} instance={firstInstance} text={mac || ''} />
                          ) : (
                            <Box key={`first-${index}`} sx={{ pl: '32px' }}>
                              <RowDataItem instance={firstInstance} text={mac || ''} hideImg />
                            </Box>
                          )
                        )
                      ) : activeRowItem?.macAddress ? (
                        <RowDataItem instance={firstInstance} text={activeRowItem.macAddress} />
                      ) : null}
                      {Array.isArray(activeRowItem?.secondMacAddress) && activeRowItem.secondMacAddress.length > 0 ? (
                        activeRowItem.secondMacAddress.map((mac, index) =>
                          index === 0 ? (
                            <RowDataItem key={`second-${index}`} instance={secondInstance} text={mac || ''} />
                          ) : (
                            <Box key={`second-${index}`} sx={{ pl: '32px' }}>
                              <RowDataItem instance={secondInstance} text={mac || ''} hideImg />
                            </Box>
                          )
                        )
                      ) : activeRowItem?.secondMacAddress ? (
                        <RowDataItem instance={secondInstance} text={activeRowItem.secondMacAddress} />
                      ) : null}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {showLastSeen && (
              <Box className="row-item">
                <Typography className="row-name">Last seen</Typography>
                <Box className="row-data">
                  {thisInstance && coverage !== 'both' ? (
                    <RowDataItem
                      instance={thisInstance}
                      text={dayjs(activeRowItem.lastSeen).format('YYYY-MM-DD HH:MM:ss')}
                      lastSeenMissing={activeRowItem.lastSeen.includes('0001-01-01')}
                    />
                  ) : (
                    <>
                      {activeRowItem?.lastSeen && !activeRowItem.lastSeen.includes('0001-01-01') && (
                        <RowDataItem
                          instance={firstInstance}
                          text={dayjs(activeRowItem.lastSeen).format('YYYY-MM-DD HH:MM:ss')}
                          lastSeenMissing={false}
                        />
                      )}
                      {activeRowItem?.secondLastSeen && !activeRowItem.secondLastSeen.includes('0001-01-01') && (
                        <RowDataItem
                          instance={secondInstance}
                          text={dayjs(activeRowItem.secondLastSeen).format('YYYY-MM-DD HH:MM:ss')}
                          lastSeenMissing={false}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {showOperatingSystem && (
              <Box className="row-item">
                <Typography className="row-name">Operating system</Typography>
                <Box className="row-data">
                  {thisInstance && coverage !== 'both' ? (
                    <RowDataItem instance={thisInstance} text={activeRowItem?.operatingSystem || ''} />
                  ) : (
                    <>
                      {activeRowItem?.operatingSystem && (
                        <RowDataItem instance={firstInstance} text={activeRowItem.operatingSystem} />
                      )}
                      {activeRowItem?.secondOperatingSystem && (
                        <RowDataItem instance={secondInstance} text={activeRowItem.secondOperatingSystem} />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {showAssetId && (
              <Box className="row-item">
                <Typography className="row-name">Asset ID</Typography>
                <Box className="row-data">
                  {thisInstance && coverage !== 'both' ? (
                    <RowDataItem instance={thisInstance} text={activeRowItem?.assetId || ''} />
                  ) : (
                    <>
                      {activeRowItem?.assetId && <RowDataItem instance={firstInstance} text={activeRowItem.assetId} />}
                      {activeRowItem?.secondAssetId && (
                        <RowDataItem instance={secondInstance} text={activeRowItem.secondAssetId} />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

export default RowDataDrawer
